import { type ComponentProps } from 'react';
import { CustomerReviewCard } from '@hungryroot/customer-reviews';
import { Text, Heading, Paragraph } from '@hungryroot/ramen';
import { Star } from './Star';

import styles from './Testimonials.module.css';

const DEFAULT_TESTIMONIALS = [
  {
    reviewCallOut: 'Quick, healthy, safe',
    review:
      'I have a wheat allergy and gluten intolerance. I was going to multiple stores to get everything I needed and buying more than I needed, which led to so much waste. This is quick, healthy, safe, and saves me $$$.',
    tags: ['Gluten-free', 'Soy-free'],
    name: 'Melissa H.',
    location: 'Ohio',
    bio: 'Enjoys sweets',
    verifiedText: 'Verified Customer',
  },
  {
    reviewCallOut: "End the “what's for dinner?” stress",
    review:
      'We wanted to eat more veggies without them rotting in the fridge, eat meatless meals occasionally, have easy prep to discourage getting takeout, end the “what’s for dinner?” stress, and hopefully lower our cholesterol and lose a little weight. Two years in, we’ve accomplished all that and are still loving it.',
    tags: ['Shellfish-free'],
    name: 'Jenifer W.',
    location: 'Maryland',
    bio: 'Loves spicy food',
    verifiedText: 'Verified Customer',
  },
  {
    reviewCallOut: 'Healthy without having to think',
    review:
      'When I first started my job, I had crazy stressful work days, and I needed a way to make something healthy without having to think. Hungryroot made it healthy, simple, and quick!',
    tags: ['Feeding 2', 'Omnivore'],
    name: 'Katie N.',
    location: 'Vermont',
    bio: 'Dislikes cilantro, loves chocolate',
    verifiedText: 'Verified Customer',
  },
] satisfies CustomerTestimonials;

type CustomerTestimonials = Omit<
  ComponentProps<typeof CustomerReviewCard>,
  'background' | 'imgSrc' | 'maxReviewChars'
>[];

type Props = {
  testimonials?: CustomerTestimonials;
};

export function Testimonials({ testimonials = DEFAULT_TESTIMONIALS }: Props) {
  return (
    <section className={styles.container}>
      <header className={styles.header}>
        <svg
          width="62"
          height="62"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M40 24H8V46H40V24Z"
            fill="#F0CA9E"
            stroke="currentColor"
            strokeWidth="2"
            strokeMiterlimit="10"
          />
          <path
            d="M18.763 40C19.1685 37.3537 21.5877 35.4394 24.2515 35.559C26.7369 35.6707 28.8628 37.5265 29.2407 39.9885H34C33.9503 39.5941 33.1513 34.1055 28.1566 31.8669C25.1379 30.5136 22.3187 31.1011 21.5509 31.289C18.101 32.1336 15.599 34.6807 14.5673 37.3811C14.1738 38.4101 14.0432 39.3318 14 39.9903C15.588 39.9938 17.175 39.9965 18.763 40Z"
            fill="#DD5240"
            stroke="currentColor"
            strokeWidth="2"
            strokeMiterlimit="10"
          />
          <path
            d="M31.8254 4.51896C31.7403 4.27155 30.9992 2.45409 29.0418 2.1504C26.9771 1.83146 25.8433 2.72729 25.2069 3.35461C24.8639 3.69348 24.2158 4.4275 23.9972 4.46737C23.7798 4.4275 23.26 3.65009 22.9353 3.28308C22.4745 2.76247 21.177 1.58405 18.9643 2.1504C17.054 2.64052 16.2684 4.27038 16.1833 4.51896C15.7028 5.91782 16.2265 7.37531 17.0619 8.81872C18.9381 12.0655 22.8149 14.5971 23.3949 14.8328C23.9396 15.1177 24.2459 14.984 24.6112 14.8328C25.1912 14.5971 29.5512 11.4675 30.9455 8.81872C31.7193 7.34834 32.3072 5.91782 31.8254 4.51896Z"
            fill="#DD5240"
            stroke="currentColor"
            strokeWidth="2"
            strokeMiterlimit="10"
          />
          <path
            d="M16.0366 21.0628L11.8621 13H3L7.72532 23.8389L16.0148 20.9899H31.9614L40.2747 24L45 13.1611H36.1379L32.025 21.0628"
            fill="#F0CA9E"
          />
          <path
            d="M16.0366 21.0628L11.8621 13H3L7.72532 23.8389L16.0148 20.9899H31.9614L40.2747 24L45 13.1611H36.1379L32.025 21.0628"
            stroke="currentColor"
            strokeWidth="2"
            strokeMiterlimit="10"
          />
        </svg>

        <Heading className={styles.title} variant="HeroAlt">
          America's most loved{' '}
          <span className={styles.nowrap}>food delivery service</span>
        </Heading>
        <Paragraph className={styles['based-on']} variant="caption">
          Based on average dollars spent per customer
        </Paragraph>
      </header>
      <div className={styles.background}>
        <section className={styles.main}>
          {testimonials.map((testimonial) => {
            return (
              <CustomerReviewCard
                {...testimonial}
                key={testimonial.name}
                contentClassName={styles['testimonial-content']}
                testimonialContainerClassName={styles['testimonial-container']}
                className={styles.card}
                truncateReview={false}
              />
            );
          })}
        </section>
        <footer className={styles.footer}>
          <div className={styles.ratings}>
            <Heading variant="HeroAlt">4.9</Heading>
            <div className={styles.stars}>
              <Star />
              <Star />
              <Star />
              <Star />
              <Star />
            </div>
          </div>
          <Text>4.9 in the Apple app store</Text>
        </footer>
      </div>
    </section>
  );
}
